import { ScrollStory, StageCanvas, StageCard, StagePanels, type StoryBeat } from '../../../../components/ScrollStory';

/**
 * "How it works" for Bot Anomaly Detection & Email Verification — the real pipeline a single
 * address travels inside one `POST /v1/emails` call, canonicalize → verdict. Every step, field
 * and threshold is drawn from the detection code (DetectionService.ingestOne + packages/shared
 * detection: canonicalize, score, disposition). The worked example follows one member of a bot
 * wave — `jdoe83917@acme.co` — so the signals, cluster and score line up to a real REVIEW verdict.
 */

const STAGES = ['Canonicalize', 'Read the shape', 'Cluster', 'Verify', 'Score', 'Result'];

type Beat = StoryBeat & { detail: React.ReactNode };

const BEATS: Beat[] = [
  {
    n: 1,
    kicker: 'Canonicalize',
    title: 'One mailbox, many disguises',
    body: (
      <>
        Every address is lowercased, its <span className="font-mono">+tag</span> stripped, and Gmail dots folded — so a
        pile of disguised aliases collapses to a single canonical mailbox key. That key is what everything downstream
        dedupes on.
      </>
    ),
    facts: ['lowercase', 'strip +tag', 'fold gmail dots'],
    detail: (
      <StageCard head="Canonicalize → one key">
        <div className="font-mono text-[11px] md:text-[12.5px]">
          <div className="text-faint">Jdoe83917+ref@Acme.co</div>
          <div className="text-accent">→ jdoe83917@acme.co</div>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">
          lowercase · strip +tag · fold gmail / googlemail dots
        </div>
      </StageCard>
    ),
  },
  {
    n: 2,
    kicker: 'Read the shape',
    title: "Read the handle's shape",
    body: (
      <>
        From just the local-part we pull a few structural signals — length, digit ratio, the letters-only skeleton,
        repeated-character runs. Machine-minted handles look measurably different from human ones.
      </>
    ),
    facts: ['len', 'digit ratio', 'skeleton', 'repeats'],
    detail: (
      <StageCard head="Local-part features">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[11px] md:text-[12.5px]">
          <Kv k="len" v="9" />
          <Kv k="digits" v="56%" hot />
          <Kv k="skeleton" v="jdoe" />
          <Kv k="repeats" v="0" />
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">high digit ratio → structural signal</div>
      </StageCard>
    ),
  },
  {
    n: 3,
    kicker: 'Cluster',
    title: 'Catch the whole bot wave',
    body: (
      <>
        Handles are bucketed by their first letters, then compared by <b className="font-semibold text-txt">edit
        distance</b> against recent sign-ups in your list. Lookalikes join a cluster — and exact same-mailbox aliases
        are caught outright. The bigger the cluster, the higher the risk.
      </>
    ),
    facts: ['bucket "jdoe#"', 'edit distance ≤ 3', 'cluster + merge'],
    detail: (
      <StageCard head="Find the lookalikes">
        <div className="flex flex-wrap gap-1.5 font-mono text-[10.5px] md:text-[11.5px]">
          {['jdoe11024', 'jdoe55291', 'jdoe83917', 'jdoe70318', 'jdoe22947'].map((h) => (
            <span
              key={h}
              className={`rounded border px-1.5 py-0.5 ${
                h === 'jdoe83917' ? 'border-review/50 bg-review/10 text-review' : 'border-border text-muted'
              }`}
            >
              {h}
            </span>
          ))}
        </div>
        <div className="mt-2 font-mono text-[10.5px] text-faint md:text-[11px]">cluster clu_8f · 6 members</div>
      </StageCard>
    ),
  },
  {
    n: 4,
    kicker: 'Verify',
    title: 'Check the mailbox is real',
    body: (
      <>
        In parallel, a deliverability check runs: MX / DNS and disposable-domain matching built-in, or a paid provider
        for authoritative mailbox, spamtrap, role and no-reply signals. Verdicts cache per mailbox for 7 days.
      </>
    ),
    facts: ['MX / disposable', 'spamtrap · role', 'cached 7 days'],
    detail: (
      <StageCard head="Verify the mailbox">
        <div className="flex flex-wrap items-center gap-2 text-[12px] md:text-[13px]">
          <span className="font-mono text-muted">MX acme.co ✓</span>
          <span className="font-mono text-muted">· not disposable</span>
          <span className="rounded-full bg-allow/15 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.06em] text-allow">
            deliverable
          </span>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">
          built-in DNS check · paid providers add spamtrap / role / no-reply
        </div>
      </StageCard>
    ),
  },
  {
    n: 5,
    kicker: 'Score',
    title: 'Sum the signals, pick an action',
    body: (
      <>
        Each signal adds points to a 0–100 risk score, which maps to{' '}
        <span className="font-mono text-allow">allow</span>, <span className="font-mono text-verify">verify</span> or{' '}
        <span className="font-mono text-review">review</span>. Reasons ship as plain buckets, never the raw rules — so
        bot operators can&apos;t reverse-engineer the scoring.
      </>
    ),
    facts: ['0–100 score', 'allow · verify · review', 'reason buckets'],
    detail: (
      <StageCard head="Score → action">
        <div className="font-mono text-[11.5px] md:text-[12.5px]">
          <ScoreRow label="structural (digit ratio)" pts="+25" />
          <ScoreRow label="near-duplicates (×6)" pts="+55" />
          <div className="mt-1 flex items-center justify-between border-t border-bordersoft pt-1">
            <span className="font-semibold">score 80</span>
            <span className="rounded-full bg-review/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-review">
              review
            </span>
          </div>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">thresholds: verify ≥ 35 · review ≥ 65</div>
      </StageCard>
    ),
  },
  {
    n: 6,
    kicker: 'Result',
    title: 'One verdict, back in the response',
    body: (
      <>
        Action and verdict combine into a disposition — <span className="font-mono">accepted</span>,{' '}
        <span className="font-mono">pending</span> or <span className="font-mono">declined</span> (a spamtrap always
        declines). It all comes back in one <span className="font-mono">EmailResult</span>, and{' '}
        <span className="font-mono">emails.scored</span> fires so your systems react in real time.
      </>
    ),
    facts: ['accepted · pending · declined', 'EmailResult', 'emails.scored'],
    detail: (
      <StageCard head="Final result — EmailResult">
        <pre className="overflow-x-auto font-mono text-[10.5px] leading-relaxed text-txt md:text-[12px]">
          <code>{`{ "action": "review", "disposition": "pending",
  "score": 80, "reasons": ["structural","near_duplicates"],
  "cluster": { "id": "clu_8f", "size": 6 },
  "verification": { "status": "deliverable" } }`}</code>
        </pre>
      </StageCard>
    ),
  },
];

export function BotDetectionStory() {
  const panels = BEATS.map((b) => ({ n: b.n, content: b.detail }));
  return (
    <section className="mt-20" aria-label="How bot detection works">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-panel px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-accent">
          <span className="h-1.5 w-1.5 rounded-full bg-accent" /> How it works
        </span>
        <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">
          Inside one <span className="font-mono text-accent">/v1/emails</span> call — address to verdict
        </h2>
        <p className="mt-3 text-[14.5px] leading-relaxed text-muted">
          Follow one member of a bot wave — <span className="font-mono">jdoe83917@acme.co</span> — through the real
          pipeline, from canonicalization to the disposition your systems act on.
        </p>
      </div>

      <ScrollStory
        beats={BEATS}
        className="mt-6"
        renderStage={(beat, p) => (
          <StageCanvas>
            <div className="absolute inset-x-4 top-4 md:inset-x-6 md:top-6">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.12em] text-faint md:text-[11px]">
                <span>Detection pipeline</span>
                <span>step {beat}/6</span>
              </div>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-border">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-accent to-allow transition-all duration-500"
                  style={{ width: `${(beat / 6) * 100}%` }}
                />
              </div>
              <div className="mt-2 font-mono text-[12px] text-accent md:text-[13px]">{STAGES[beat - 1]}</div>
              <span className="sr-only">{`Progress ${Math.round(p * 100)}%`}</span>
            </div>
            <StagePanels beat={beat} panels={panels} />
          </StageCanvas>
        )}
      />
    </section>
  );
}

function Kv({ k, v, hot }: { k: string; v: string; hot?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-faint">{k}</span>
      <span className={hot ? 'font-bold text-verify' : 'text-txt'}>{v}</span>
    </div>
  );
}

function ScoreRow({ label, pts }: { label: string; pts: string }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-muted">{label}</span>
      <span className="text-verify">{pts}</span>
    </div>
  );
}
